import { useLivekitConnection } from './useLivekitConnection'
import { useLivekitAudio } from './useLivekitAudio'
import { useLivekitContext } from './useLivekitContext'
import { useLivekitCore } from './useLivekitCore'
import type { ExtendedRoom, RpcRequest, UseLivekitOptions } from './types'
import { getCurrentScope, onScopeDispose } from 'vue'

import {
  ParticipantKind,
  RoomEvent,
  type RemoteAudioTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
  type RemoteTrack,
} from 'livekit-client'
import type {
  Envelope,
  SegmentPayload,
  FlagDeltaPayload,
  LifecycleEventPayload,
  UpdateChatContextPayload,
  ToolLatencyPayload,
} from '@/types/LivekitContext.ts'

const isEnvelopeFormat = (data: unknown): data is Envelope => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    'payload' in data &&
    ['segment', 'flag_delta', 'lifecycle_event', 'tool_latency'].includes((data as Envelope).type)
  )
}

export const useLivekit = (options: UseLivekitOptions = { audio: true, context: true }) => {
  const { audio = true, onToolLatency, formatToolLatency } = options

  const { room, activeSpeakers, lastSpeaker, disconnectedRooms } = useLivekitCore()

  const connection = useLivekitConnection()
  const audioComposable = audio ? useLivekitAudio() : undefined
  const contextComposable = useLivekitContext()

  const extendedRoom = room as ExtendedRoom

  if (!extendedRoom.listenersAttachedCallLifecycle) {
    extendedRoom.listenersAttachedCallLifecycle = true
    room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      if (participant.kind === ParticipantKind.SIP || participant.kind === ParticipantKind.AGENT) {
        disconnectedRooms.value.add(room.name)
      }
    })
  }

  if (onToolLatency) {
    extendedRoom.toolLatencyHandlers ??= new Set()
    extendedRoom.toolLatencyHandlers.add(onToolLatency)

    if (getCurrentScope()) {
      onScopeDispose(() => extendedRoom.toolLatencyHandlers?.delete(onToolLatency))
    }
  }

  if (audio && audioComposable && !extendedRoom.listenersAttachedAudio) {
    extendedRoom.listenersAttachedAudio = true

    // RPC method might already be registered by useLiveTest.ts
    try {
      room.registerRpcMethod('updateChatContext', (data: RpcRequest): Promise<'ok'> => {
        const parsed = JSON.parse(data.payload) as Envelope | Partial<UpdateChatContextPayload>
        if (isEnvelopeFormat(parsed)) {
          // Envelope format (v2)
          switch (parsed.type) {
            case 'segment': {
              const segmentPayload = parsed.payload as SegmentPayload
              if (segmentPayload.call_id) {
                contextComposable.setCallId(segmentPayload.call_id)
              }
              for (const segment of segmentPayload.history) {
                contextComposable.mergeSegment(segment)
              }
              break
            }
            case 'flag_delta':
              contextComposable.mergeFlagDelta(parsed.payload as FlagDeltaPayload)
              break
            case 'lifecycle_event': {
              const lifecyclePayload = parsed.payload as LifecycleEventPayload
              contextComposable.addLifecycleEvent(lifecyclePayload)
              // Mark room as ended when call_ended is received
              if (lifecyclePayload.type === 'call_ended') {
                disconnectedRooms.value.add(room.name)
              }
              break
            }
            case 'tool_latency': {
              const latencyPayload = parsed.payload as ToolLatencyPayload
              contextComposable.addToolLatencyEvent(
                latencyPayload.tool,
                latencyPayload.latency_ms,
                latencyPayload.timestamp,
                formatToolLatency?.(latencyPayload.tool, latencyPayload.latency_ms),
              )
              extendedRoom.toolLatencyHandlers?.forEach((handler) => {
                handler(latencyPayload.tool, latencyPayload.latency_ms)
              })
              break
            }
          }
        } else if (parsed.history) {
          // Legacy format - DELETE THIS BLOCK when v2 only
          contextComposable.addContextHistory(parsed.history)
        }

        return Promise.resolve('ok')
      })
    } catch {
      // Already registered, ignore
    }

    room
      .on(
        RoomEvent.TrackSubscribed,
        (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
          if (track.kind === 'audio') {
            audioComposable.attachAudioTrack(track as RemoteAudioTrack, participant)
          }
        },
      )
      .on(RoomEvent.TrackUnsubscribed, (_track, _pub, participant) => {
        audioComposable.detachAudioTrack(participant)
      })
  }

  // if (transcriptEnabled && contextComposable && !extendedRoom.listenersAttachedTranscript) {
  // extendedRoom.listenersAttachedTranscript = true

  // room.on(RoomEvent.TranscriptionReceived, (segments, participant) => {
  //   contextComposable.handleTranscription(
  //     segments as LivekitTranscriptionSegment[],
  //     participant,
  //     lastSpeaker.value ?? undefined,
  //   )
  // })
  // }

  if (!extendedRoom.listenersAttachedSpeakers) {
    extendedRoom.listenersAttachedSpeakers = true
    room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      activeSpeakers.value = speakers.map((s) => s.identity)
      if (speakers.length) lastSpeaker.value = speakers[0].identity
    })
  }

  return {
    ...connection,
    audio: audioComposable,
    context: contextComposable,
  }
}
